import { useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { ModalKind, DismissBehavior } from "@/lib/modalRegistry";

export interface AppModalProps {
  kind: ModalKind;
  isOpen: boolean;
  title: string;
  body: string;
  icon?: React.ReactNode;
  imageUrl?: string;
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  onClose: (method?: "escape" | "backdrop" | "x_button") => void;
  dismissBehavior?: DismissBehavior;
  size?: 'sm' | 'md' | 'lg';
}

const TITLE_ID = "app-modal-title";
const BODY_ID = "app-modal-body";

export function AppModal({
  isOpen,
  title,
  body,
  icon,
  imageUrl,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  onClose,
  dismissBehavior = 'DISMISS',
  size = 'md',
}: AppModalProps) {
  const primaryRef = useRef<HTMLButtonElement>(null);
  const dismissAllowed = dismissBehavior === 'DISMISS';

  useEffect(() => {
    if (isOpen) {
      const frame = requestAnimationFrame(() => {
        primaryRef.current?.focus();
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open && dismissAllowed) {
      onClose("backdrop");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (!dismissAllowed) {
        e.preventDefault();
        e.stopPropagation();
      } else {
        onClose("escape");
      }
    }
  };

  const sizeClass = imageUrl
    ? 'sm:max-w-lg md:max-w-2xl p-0 overflow-hidden'
    : size === 'sm' ? 'sm:max-w-sm'
    : size === 'lg' ? 'sm:max-w-lg'
    : 'sm:max-w-md';

  const ctaRow = (
    <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
      {secondaryLabel && (
        <Button
          variant="outline"
          onClick={() => { onSecondary?.(); }}
          className="flex-1"
          data-testid="app-modal-secondary"
        >
          {secondaryLabel}
        </Button>
      )}
      <Button
        ref={primaryRef}
        onClick={onPrimary}
        className="flex-1"
        data-testid="app-modal-primary"
      >
        {primaryLabel}
      </Button>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={sizeClass}
        aria-labelledby={TITLE_ID}
        aria-describedby={BODY_ID}
        data-testid="app-modal"
        onKeyDown={handleKeyDown}
        hideCloseButton={!dismissAllowed}
      >
        {imageUrl ? (
          <div className="flex flex-col md:flex-row">
            <div className="w-full md:w-1/3 flex-shrink-0">
              <img
                src={imageUrl}
                alt=""
                className="w-full h-48 md:h-full object-cover"
                data-testid="app-modal-image"
                aria-hidden="true"
              />
            </div>
            <div className="flex flex-col gap-4 p-6 flex-1">
              <DialogHeader>
                <DialogTitle id={TITLE_ID} data-testid="app-modal-title">{title}</DialogTitle>
                <DialogDescription id={BODY_ID} data-testid="app-modal-body" className="text-sm text-muted-foreground leading-relaxed">
                  {body}
                </DialogDescription>
              </DialogHeader>
              {ctaRow}
            </div>
          </div>
        ) : (
          <div className="p-6 flex flex-col gap-4">
            {icon && (
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-muted mx-auto" data-testid="app-modal-icon">
                {icon}
              </div>
            )}
            <DialogHeader>
              <DialogTitle
                id={TITLE_ID}
                data-testid="app-modal-title"
                className={icon ? "text-center" : undefined}
              >
                {title}
              </DialogTitle>
              <DialogDescription
                id={BODY_ID}
                data-testid="app-modal-body"
                className={`text-sm text-muted-foreground leading-relaxed${icon ? " text-center" : ""}`}
              >
                {body}
              </DialogDescription>
            </DialogHeader>
            {ctaRow}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
